import React from "react";
import { Link } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { useT } from "~/lib/i18n";
import { formatRelative } from "~/lib/utils";
import { cn } from "~/lib/utils";

/**
 * Authenticated user landing — lists their saved scans, with a CTA to
 * create a new one. The /scans page reads from `me.scans` which returns
 * 401 for anonymous, in which case we render a "sign in" empty state.
 */
export default function Home(): React.JSX.Element {
  const { t, numberLocale } = useT();
  const scansQ = trpc.scan.listMine.useQuery(undefined, { retry: false });
  const isAuthed = !(scansQ.error?.data?.code === "UNAUTHORIZED");

  return (
    <div className="w-full max-w-[1100px] mx-auto px-5 py-10">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-[34px] leading-[42px] text-ink-primary">
          {t("web.scans.title")}
        </h1>
        <Link
          to="/scan/new"
          className="inline-flex h-9 items-center gap-2 rounded-[4px] bg-[var(--accent)] px-3.5 text-[13.5px] font-medium text-[var(--ink-on-accent)] tracking-[0.005em] transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-[var(--accent-hover)]"
        >
          {t("web.scans.newCta")}
        </Link>
      </div>

      <p className="mt-3 text-[15px] text-ink-secondary max-w-[60ch]">
        {t("web.scans.subtitle")}
      </p>

      {!isAuthed && (
        <NotLoggedIn
          title={t("web.scans.notLogged.title")}
          body={t("web.scans.notLogged.body")}
          ctaLabel={t("common.signin")}
        />
      )}

      {isAuthed && scansQ.isPending && (
        <div className="mt-10 border-t border-[var(--divider-weak)] pt-6 space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[52px] rounded-[4px] bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      )}

      {isAuthed && !scansQ.isPending && (scansQ.data?.length ?? 0) === 0 && (
        <EmptyState
          title={t("web.scans.empty.title")}
          body={t("web.scans.empty.body")}
          ctaLabel={t("web.scans.newCta")}
        />
      )}

      {isAuthed && (scansQ.data?.length ?? 0) > 0 && (
        <div className="mt-10 border-t border-[var(--divider-strong)]">
          <div className="hidden sm:grid grid-cols-[1.4fr_120px_140px_120px] gap-4 px-2 py-3 border-b border-[var(--divider-weak)] text-[11px] mono uppercase tracking-[0.08em] text-ink-tertiary">
            <span>{t("web.scans.col.name")}</span>
            <span>{t("web.scans.col.status")}</span>
            <span className="text-right">{t("web.scans.col.last")}</span>
            <span className="text-right"></span>
          </div>
          {scansQ.data?.map((s) => {
            const status = t("web.scans.status.active");
            const last = s.lastRefreshAt ? formatRelative(s.lastRefreshAt as unknown as string, numberLocale) : "—";
            const countries = ((s.filters as any)?.countries ?? []).join(" · ").toUpperCase();
            const target = `/scan/${s.id}`;
            return (
              <Link
                key={s.id}
                to={target}
                className="grid grid-cols-1 sm:grid-cols-[1.4fr_120px_140px_120px] gap-1 sm:gap-4 items-start sm:items-center px-2 py-4 border-b border-[var(--divider-weak)] hover:bg-surface-hover transition-colors duration-[var(--dur-quick)]"
              >
                <div className="min-w-0">
                  <div className="text-[14.5px] text-ink-primary truncate">{s.name}</div>
                  {countries && (
                    <div className="mt-0.5 text-[11.5px] mono text-ink-tertiary tracking-[0.04em]">
                      {countries}
                    </div>
                  )}
                </div>
                <span
                  className={cn(
                    "text-[12px] tabular-nums",
                    "text-deal-super",
                  )}
                >
                  {status}
                </span>
                <span className="text-[12px] text-ink-secondary sm:text-right tabular-nums">{last}</span>
                <span className="text-[12px] text-ink-secondary sm:text-right">{t("common.view")}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotLoggedIn({
  title,
  body,
  ctaLabel,
}: {
  title: string;
  body: string;
  ctaLabel: string;
}): React.JSX.Element {
  return (
    <div className="mt-10 border-t border-[var(--divider-strong)] pt-12 flex flex-col items-start">
      <h2 className="font-display text-[22px] leading-[28px] text-ink-primary">{title}</h2>
      <p className="mt-2 text-[14px] text-ink-secondary max-w-[55ch]">{body}</p>
      <Link
        to="/login"
        className="mt-5 inline-flex h-10 items-center gap-2 rounded-[4px] bg-[var(--accent)] px-4 text-[14px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors duration-[var(--dur-quick)]"
      >
        <SignInIcon />
        <span>{ctaLabel}</span>
      </Link>
    </div>
  );
}

function EmptyState({
  title,
  body,
  ctaLabel,
}: {
  title: string;
  body: string;
  ctaLabel: string;
}): React.JSX.Element {
  return (
    <div className="mt-10 border-t border-[var(--divider-strong)] pt-12 flex flex-col items-start">
      <h2 className="font-display text-[22px] leading-[28px] text-ink-primary">{title}</h2>
      <p className="mt-2 text-[14px] text-ink-secondary max-w-[55ch]">{body}</p>
      <Link
        to="/scan/new"
        className="mt-5 inline-flex h-10 items-center gap-2 rounded-[4px] bg-[var(--accent)] px-4 text-[14px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors duration-[var(--dur-quick)]"
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

function SignInIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  );
}
